import type { PlatformProductSummary } from "@/lib/services/platform-product.service"

export type PlatformProductBrand = Pick<PlatformProductSummary, "name" | "slug" | "colors" | "logos" | "font">

const FONT_LINK_ID = "platform-product-font-link"
const FONT_STYLE_ID = "platform-product-font-face"

function hexToHsl(hex: string): string | null {
  const normalized = hex.replace("#", "").trim()
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null

  const r = Number.parseInt(expanded.slice(0, 2), 16) / 255
  const g = Number.parseInt(expanded.slice(2, 4), 16) / 255
  const b = Number.parseInt(expanded.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    if (max === r) h = 60 * (((g - b) / delta) % 6)
    if (max === g) h = 60 * ((b - r) / delta + 2)
    if (max === b) h = 60 * ((r - g) / delta + 4)
  }

  return `${Math.round((h + 360) % 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

function foregroundFor(hex: string): string {
  const normalized = hex.replace("#", "").trim()
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return "0 0% 100%"

  const r = Number.parseInt(expanded.slice(0, 2), 16)
  const g = Number.parseInt(expanded.slice(2, 4), 16)
  const b = Number.parseInt(expanded.slice(4, 6), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 150 ? "0 0% 18%" : "0 0% 100%"
}

function mutedFor(hex: string): string {
  const hsl = hexToHsl(hex)
  if (!hsl) return "270 100% 94%"

  const [h, s] = hsl.split(" ")
  return `${h} ${s} 94%`
}

function setHslVariable(root: HTMLElement, name: string, hex?: string): void {
  if (!hex) return

  const hsl = hexToHsl(hex)
  if (hsl) root.style.setProperty(name, hsl)
}

function setFont(brand: PlatformProductBrand): void {
  const font = brand.font
  const fontName = (font?.name || brand.name || "Inter").replace(/["'<>]/g, "").trim() || "Inter"
  const family = `"${fontName}", Inter, ui-sans-serif, system-ui, sans-serif`

  document.documentElement.style.setProperty("--product-font-family", family)

  document.getElementById(FONT_LINK_ID)?.remove()
  document.getElementById(FONT_STYLE_ID)?.remove()

  if (font?.source === "cdn" && font.cdnUrl) {
    const link = document.createElement("link")
    link.id = FONT_LINK_ID
    link.rel = "stylesheet"
    link.href = font.cdnUrl
    document.head.appendChild(link)
    return
  }

  if (font?.source === "file" && font.fileUrl) {
    const style = document.createElement("style")
    style.id = FONT_STYLE_ID
    style.textContent = `@font-face{font-family:"${fontName}";src:url(${JSON.stringify(font.fileUrl)});font-display:swap;}`
    document.head.appendChild(style)
  }
}

export function applyPlatformBrand(brand?: PlatformProductBrand | null): void {
  if (!brand || typeof document === "undefined") return

  const root = document.documentElement
  const colors = brand.colors

  setHslVariable(root, "--primary", colors?.primary)
  setHslVariable(root, "--primary-foreground", colors?.primaryForeground)
  setHslVariable(root, "--primary-muted", colors?.primaryMuted)
  setHslVariable(root, "--secondary", colors?.secondary)
  setHslVariable(root, "--secondary-foreground", colors?.secondaryForeground)
  setHslVariable(root, "--accent", colors?.accent)
  setHslVariable(root, "--accent-foreground", colors?.accentForeground)
  setHslVariable(root, "--muted", colors?.muted)
  setHslVariable(root, "--muted-foreground", colors?.mutedForeground)
  setHslVariable(root, "--destructive", colors?.destructive)
  setHslVariable(root, "--destructive-foreground", colors?.destructiveForeground)
  setHslVariable(root, "--popover", colors?.popover)
  setHslVariable(root, "--popover-foreground", colors?.popoverForeground)
  setHslVariable(root, "--card", colors?.card)
  setHslVariable(root, "--card-foreground", colors?.cardForeground)
  setHslVariable(root, "--success", colors?.success)
  setHslVariable(root, "--success-foreground", colors?.successForeground)
  setHslVariable(root, "--ring", colors?.primary)

  if (colors?.primary) {
    if (!colors.primaryMuted) root.style.setProperty("--primary-muted", mutedFor(colors.primary))
    if (!colors.primaryForeground) root.style.setProperty("--primary-foreground", foregroundFor(colors.primary))
  }
  if (colors?.secondary && !colors.secondaryForeground) {
    root.style.setProperty("--secondary-foreground", foregroundFor(colors.secondary))
  }
  if (colors?.accent && !colors.accentForeground) {
    root.style.setProperty("--accent-foreground", foregroundFor(colors.accent))
  }
  if (colors?.sidebarBackground) root.style.setProperty("--sidebar-background", colors.sidebarBackground)
  if (colors?.sidebarForeground) root.style.setProperty("--sidebar-foreground", colors.sidebarForeground)

  setFont(brand)
}
